// vim: ft=javascriptreact
import { useState, useEffect } from 'react'
import {
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import useAuth from '../hooks/useAuth'

const SignInForm = () => {
  const navigate = useNavigate()

  const {
    signIn,
    email,
    setEmail,
    authError,
    authNewPassword,
    authResetPassword,
    completeNewPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    resetPassword,
    forgotPassword,
    cancelForgotPassword,
    facebookSignin,
    showFacebook,
    resendCode,
  } = useAuth()
  const [password, setPassword] = useState('')
  const [signingIn, setSigningIn] = useState(false)
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [resetCode, setResetCode] = useState('')

  useEffect(() => {
    if (authNewPassword || authResetPassword) {
      setPassword('')
    }

  }, [authNewPassword, authResetPassword])

  const onSignIn = async () => {
    setSigningIn(true)
    try {
      if (await signIn(email, password)) {
        setPassword('')
        try {
          window.localStorage.setItem('email', email)
        } catch (e) {}
      } else {
        setSigningIn(false)
      }
    } catch (err) {
      setSigningIn(false);
      // Direct the user back to the sign-up confirm page
      if (err.code === 'UserNotConfirmedException') {
        const result = await resendCode(`You have not yet confirmed your email. A verification code has been resent to ${email}. Please make sure you check your junk folder if you do not see it.`)

        navigate('/auth/sign-up-confirm')
      }
    }
  }

  const onSetNewPassword = async () => {
    setSigningIn(true)
    if (await completeNewPassword(password)) {
      setPassword('')
      try {
        window.localStorage.setItem('email', email)
      } catch (e) {}
    }
    setSigningIn(false)
  }

  const onResetPassword = async () => {
    setSigningIn(true)
    if (await resetPassword(email, resetCode, password)) {
      onSignIn()
      try {
        window.localStorage.setItem('email', email)
      } catch (e) {}
    }
    setSigningIn(false)
  }

  const onForgotPassword = async () => {
    setSigningIn(true)
    try {
      if (await forgotPassword(email)) {
      }
    } catch (e) {}
    setSigningIn(false)
  }

  const onCancelForgotPassword = () => {
    cancelForgotPassword();
  }

  const canSignUp = (!!firstName && !! lastName && !!password && !!passwordConfirm && (password === passwordConfirm))
  const canResetPassword = (!!resetCode && (password === passwordConfirm))

  if (signingIn) {
    return (
      <Spinner animation="border" role="status" variant="dark">
        <span className="visually-hidden">Signing In...</span>
      </Spinner>
    )
  }

  return (
    <Form>
      {authError && <Alert variant="danger">{authError}</Alert>}

      <Form.Group className="mb-3" controlId="signin-email">
        <Form.Label>Email Address</Form.Label>
        <Form.Control type="email" placeholder="resident-only@theimperialstrata.com" value={email} readOnly={authNewPassword} onChange={(e) => setEmail(e.target.value)} />
      </Form.Group>

      {!(authNewPassword || authResetPassword) && (
        <Form.Group className="mb-3" controlId="signin-password">
          <Form.Label>Password</Form.Label>
          <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Form.Group>
      )}

      {authResetPassword && (
        <Row>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="signin-password">
              <Form.Label>Reset Code</Form.Label>
              <Form.Control type="text" value={resetCode} onChange={(e) => setResetCode(e.target.value)} />
              <Form.Text>Enter the 6 digit reset code sent to your email.</Form.Text>
            </Form.Group>
          </Col>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="password">
              <Form.Label>Password</Form.Label>
              <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Form.Text>Must be at least 8 characters and contain upper and lower case letters as well as a number.</Form.Text>
            </Form.Group>
          </Col>
          <Col sm="4">
            <Form.Group className="mb-3" controlId="password-confirm">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
      )}

      {authNewPassword && (
        <>
          <Row>
            <Col sm="6">
              <Form.Group controlId="first-name" className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  isValid={!!firstName}
                  onChange={(e) => setFirstName(e.target.value)} />
              </Form.Group>

            </Col>
            <Col sm="6">
              <Form.Group controlId="last-name" className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  isValid={!!lastName}
                  onChange={(e) => setLastName(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col sm="6">
              <Form.Group className="mb-3" controlId="password">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Form.Text>Must be at least 8 characters and contain upper and lower case letters as well as a number.</Form.Text>
              </Form.Group>

            </Col>
            <Col sm="6">
              <Form.Group className="mb-3" controlId="password-confirm">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control type="password" value={passwordConfirm} onChange={(e) => setPasswordConfirm(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {!(authNewPassword || authResetPassword) && (
        <Button variant="primary" size="lg" className="me-3 mb-1" disabled={!email || !password} onClick={onSignIn}>Sign In</Button>
      )}

      {showFacebook && <Button variant="primary" size="lg" className="me-3 mb-1" onClick={facebookSignin}>Sign In With Facebook</Button>}

      {authNewPassword && (
        <Button variant="primary" size="lg" className="me-3 mb-1" disabled={!canSignUp} onClick={onSetNewPassword}>Set New Password</Button>
      )}
      {authResetPassword && (
        <>
          <Button size="lg" className="me-3 mb-1" onClick={onCancelForgotPassword}>Cancel</Button>
          <Button variant="primary" size="lg" className="me-3 mb-1" disabled={!canResetPassword} onClick={onResetPassword}>Reset Password</Button>
        </>
      )}
      {!authNewPassword && !authResetPassword && (
        <Button variant="secondary" size="lg" className="me-3 mb-1 float-right" disabled={!email} onClick={onForgotPassword}>Forgot Password</Button>
      )}
    </Form>
  )

}

export default SignInForm
